/**
 * React hook for WebSocket connectivity
 */
import { useState, useEffect, useCallback } from 'react';
import { useUnifiedAuth } from '../auth/AuthProvider';
import websocketService, { WebSocketMessage } from '../services/websocketService';

export interface WebSocketState {
  isConnected: boolean;
  connectionState: 'disconnected' | 'connecting' | 'connected' | 'error';
  lastMessage: WebSocketMessage | null;
  error: string | null;
}

export const useWebSocket = () => {
  const { getAccessTokenSilently, isAuthenticated } = useUnifiedAuth();
  const [state, setState] = useState<WebSocketState>({
    isConnected: false,
    connectionState: 'disconnected',
    lastMessage: null,
    error: null
  });

  const handleMessage = useCallback((message: WebSocketMessage) => {
    setState(prev => ({
      ...prev,
      lastMessage: message,
      error: message.type === 'error' ? message.message || 'WebSocket error' : null
    }));
  }, []);

  const connect = useCallback(async () => {
    try {
      let token: string | undefined;
      
      if (isAuthenticated) {
        try {
          token = await getAccessTokenSilently();
        } catch (error) {
          console.warn('Failed to get access token for WebSocket:', error);
          // Continue without token for unauthenticated connection
        }
      }

      await websocketService.connect(token);
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to connect to WebSocket server'
      }));
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  const disconnect = useCallback(() => {
    websocketService.disconnect();
  }, []);

  const sendMessage = useCallback((message: any) => {
    websocketService.send(message);
  }, []);

  const sendPing = useCallback(() => {
    websocketService.sendPing();
  }, []);

  useEffect(() => {
    websocketService.addEventHandler(handleMessage);

    // Update connection state based on WebSocket service state
    const updateConnectionState = () => {
      const connectionState = websocketService.getConnectionState();
      setState(prev => ({
        ...prev,
        isConnected: websocketService.isConnected(),
        connectionState
      }));
    };

    // Check connection state periodically
    const interval = setInterval(updateConnectionState, 1000);

    return () => {
      websocketService.removeEventHandler(handleMessage);
      clearInterval(interval);
    };
  }, [handleMessage]);

  // Auto-connect when component mounts
  useEffect(() => {
    connect();
    
    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  return {
    ...state,
    connect,
    disconnect,
    sendMessage,
    sendPing
  };
};

export default useWebSocket;